import { useEffect, useState } from "react";
import {
  useGobierno,
  usePropuesta,
  useAccionesGobierno,
  useTienenCodigo,
  usePendientes,
  type Pendiente,
} from "../hooks/useGobierno";
import type { RedKeel } from "../config/networks";
import { Giro } from "./Modal";
import { ModalOperacion, type EstadoOp } from "./ModalOperacion";
import { useT } from "../lib/i18n";

/**
 * Cuenta atrás que se mueve. Un «faltan 3 h» pintado una vez y congelado es peor que no ponerlo:
 * el plazo del timelock es exactamente lo que hay que vigilar, y con un número quieto no se sabe si
 * la pantalla está viva o se quedó colgada hace media hora.
 */
function Cuenta({ hasta }: { hasta: number }) {
  const t = useT();
  const [ahora, setAhora] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const iv = setInterval(() => setAhora(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(iv);
  }, []);

  const s = Math.max(0, hasta - ahora);
  if (s === 0) return <strong>{t("ya")}</strong>;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sg = s % 60;
  // Los segundos solo en la última hora: antes son ruido, y en el último tramo son justo el dato.
  const txt = d > 0 ? t("{d} d {h} h {m} min", { d, h, m }) : h > 0 ? t("{h} h {m} min", { h, m }) : t("{m} min {s} s", { m, s: sg });
  return <strong className="mono">{txt}</strong>;
}

const fecha = (ts: number) =>
  new Date(ts * 1000).toLocaleString(undefined, {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });

/**
 * Publicar lógica nueva, con el retardo del timelock, firmando con la wallet.
 *
 * Solo aparece si la wallet conectada es la dueña del timelock de esa red: para cualquier otra no
 * existe esta pantalla. No es seguridad —el contrato ya rechaza a quien no manda— es no enseñar
 * botones que van a revertir.
 *
 * LO QUE ESTA PANTALLA NO HACE, a propósito: no deja escribir una llamada cualquiera. Solo compone
 * `setLogic(impl, rebalancer)`, que es el cambio que se hace cada vez. Un campo libre de calldata en
 * una pantalla web es una forma excelente de firmar por error algo que no se entiende.
 */
export function Gobierno({ red, alVolver }: { red: RedKeel; alVolver: () => void }) {
  const t = useT();
  const { data: g, isLoading } = useGobierno(red);
  const [impl, setImpl] = useState("");
  const [logica, setLogica] = useState("");
  const { data: p } = usePropuesta(red, g?.timelock, impl, logica);
  const { data: cod, isLoading: mirandoCodigo } = useTienenCodigo(red, impl, logica);
  const acc = useAccionesGobierno(red, g?.timelock);
  const { data: cola, isLoading: cargandoCola } = usePendientes(red, g?.timelock);

  const dir = (s: string) => /^0x[0-9a-fA-F]{40}$/.test(s);
  const formatoOk = dir(impl) && dir(logica);
  // No basta con el formato, ni siquiera con que haya código: tienen que ser NUESTROS contratos, en
  // ESTA red. Una dirección de otra cadena puede tener encima un contrato ajeno.
  const sonNuestros = !!cod?.implEsVault && !!cod?.logicaEsRebalancer && !cod?.pareceIntercambiado;
  const listoParaEncolar = formatoOk && sonNuestros;
  const ocupado = !!acc.haciendo;

  // Estado para el modal de proceso (logo → cargando → éxito/error), común a los botones de arriba y a
  // los de la cola. El error manda sobre «hecho»: cuando la tx entró pero el RPC no confirmó, ambos se
  // ponen, y lo importante es el mensaje que dice «no vuelvas a firmar».
  const estadoOp: EstadoOp | null = acc.haciendo
    ? {
        fase: "cargando",
        titulo: acc.haciendo === "encolar" ? t("Anunciando el cambio") : t("Aplicando el cambio"),
        sub:
          acc.haciendo === "encolar"
            ? t("Confírmalo en tu wallet. Se registra el anuncio en el timelock; nada cambia hasta pasado el plazo.")
            : t("Confírmalo en tu wallet. Se aplica el cambio ya anunciado."),
      }
    : acc.error
      ? { fase: "error", titulo: "", mensaje: acc.error }
      : acc.hecho
        ? {
            fase: "ok",
            titulo: acc.hecho === "encolar" ? t("Cambio anunciado") : t("Cambio aplicado"),
            sub:
              acc.hecho === "encolar"
                ? t("Vuelve cuando pase el plazo para aplicarlo.")
                : t("La factory ya publica la lógica nueva. Los vaults nuevos nacerán con ella."),
          }
        : null;

  const horas = (s: number) => {
    if (s <= 0) return t("ya");
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? t("{h} h {m} min", { h, m }) : t("{m} min", { m });
  };

  if (isLoading) return <p className="detalle">{t("Leyendo el gobierno de {red}…", { red: red.etiqueta })}</p>;
  if (!g) return <p className="aviso error">{t("No pude leer el gobierno de esta red.")}</p>;

  // LA COLA SE ENSEÑA MANDE QUIEN MANDE. El timelock no existe para que el dueño lleve la cuenta de
  // sus propios anuncios: existe para que un cambio de gobierno se VEA VENIR con un día de margen.
  // Esconder lo anunciado a quien no gobierna sería quitarle justo eso — solo se le quitan los
  // botones, que revertirían.
  const panelCola = (
    <ColaPendientes
      cola={cola} cargando={cargandoCola} red={red}
      puedoAplicar={g.puedoGobernar} acc={acc} graciaSegundos={g.graciaSegundos}
    />
  );

  if (!g.puedoGobernar) {
    return (
      <section className="tarjeta">
        <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
        <h2>{t("Gobierno")} · {red.etiqueta}</h2>
        <p className="detalle">
          {t("Esta wallet no gobierna la factory de {red}. Manda el timelock", { red: red.etiqueta })}{" "}
          <code>{g.timelock}</code>, {t("y su dueño es")} <code>{g.duenoDelTimelock}</code>. {t("Puedes ver igualmente lo que hay anunciado: para eso está el retardo.")}
        </p>
        {panelCola}
      </section>
    );
  }

  return (
    <section className="tarjeta">
      <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
      <h2>{t("Gobierno")} · {red.etiqueta}</h2>

      <p className="detalle">
        {t("Todo cambio de gobierno se")} <strong>{t("anuncia")}</strong>, {t("espera")}{" "}
        <strong>{horas(g.delaySegundos)}</strong> {t("y solo entonces se aplica. El plazo no se puede acortar: es lo que permite a cualquier dueño ver venir el cambio y sacar su dinero antes.")}
      </p>

      <div className="resumen-crear">
        <div><span className="k">Factory</span><span className="v">{red.factory}</span></div>
        <div><span className="k">{t("Timelock (el admin)")}</span><span className="v">{g.timelock}</span></div>
        <div><span className="k">{t("Implementación publicada hoy")}</span><span className="v">{g.implActual}</span></div>
        <div><span className="k">{t("Lógica publicada hoy")}</span><span className="v">{g.logicaActual}</span></div>
      </div>

      {panelCola}

      <h3>{t("Anunciar un cambio nuevo")}</h3>

      <div className="campo-modal">
        <label>
          <span className="et">{t("KeelVault (implementación) nueva")}</span>
          <span className="caja">
            <input value={impl} onChange={(e) => setImpl(e.target.value.trim())}
              placeholder="0x…" disabled={ocupado} spellCheck={false} />
          </span>
        </label>
        <label>
          <span className="et">{t("KeelRebalancer (lógica) nueva")}</span>
          <span className="caja">
            <input value={logica} onChange={(e) => setLogica(e.target.value.trim())}
              placeholder="0x…" disabled={ocupado} spellCheck={false} />
          </span>
        </label>
        <p className="detalle">
          {t("Las dos salen del despliegue")} (<code>deploy-logic.sh</code>). {t("Tienen que ser")}
          <strong> {t("idénticas")}</strong> {t("al anunciar y al aplicar: el identificador de la propuesta es el hash de la llamada, así que un carácter distinto es otra propuesta.")}
        </p>
      </div>

      {/* EL AVISO QUE IMPIDE ROMPER LA FACTORY. Se despliega en varias cadenas el mismo día y las
          direcciones se parecen todas; pegar las de otra red es el error natural, no el raro. */}
      {formatoOk && !mirandoCodigo && cod && !sonNuestros && (
        <div className="aviso error">
          <p className="detalle">
            {cod.pareceIntercambiado ? (
              <>
                <strong>{t("Están cambiadas de sitio.")}</strong> {t("Lo que pusiste como implementación responde a")} <code>CONVERT_FEE_BPS</code>, {t("y eso solo existe en la lógica. Intercámbialas.")}
              </>
            ) : !cod.implTieneCodigo || !cod.logicaTieneCodigo ? (
              <>
                <strong>{t("Eso no existe en {red}.", { red: red.etiqueta })}</strong>{" "}
                {!cod.implTieneCodigo && !cod.logicaTieneCodigo
                  ? t("Ninguna de las dos direcciones tiene un contrato aquí")
                  : !cod.implTieneCodigo
                    ? t("La implementación no tiene contrato aquí")
                    : t("La lógica no tiene contrato aquí")}
                {" "}— {t("casi seguro son las de otra cadena.")} <strong>{t("Cada red tiene su propio par.")}</strong>
              </>
            ) : (
              <>
                <strong>{t("Hay un contrato ahí, pero no es el nuestro.")}</strong>{" "}
                {!cod.logicaEsRebalancer
                  ? t("La lógica no responde a CONVERT_FEE_BPS, así que no es un KeelRebalancer de esta versión.")
                  : t("La implementación no lleva withdrawTo(bool), así que no es un KeelVault de esta versión.")}
                {" "}{t("Direcciones de otra cadena pueden tener encima un contrato ajeno: pasa, y sin esta comprobación se publicaría igual.")}
              </>
            )}
          </p>
          <p className="detalle">
            {t("Publicarlo")} <strong>{t("no fallaría al firmar")}</strong>. {t("Fallaría después, con dinero dentro.")}
          </p>
        </div>
      )}

      {p && listoParaEncolar && (
        <div className={`aviso ${p.lista ? "" : "error"}`}>
          <p className="detalle">
            {!p.encolada && <>{t("Esta llamada")} <strong>{t("no está anunciada")}</strong> {t("todavía.")}</>}
            {p.encolada && !p.lista && !p.caducada && (
              <>{t("Anunciada. Se puede aplicar en")} <strong>{horas(p.faltan)}</strong>.</>
            )}
            {p.lista && <>{t("Ya se puede")} <strong>{t("aplicar")}</strong>.</>}
            {p.caducada && <>{t("La propuesta")} <strong>{t("caducó")}</strong>: {t("hay que volver a anunciarla.")}</>}
          </p>
        </div>
      )}

      <div className="acciones">
        <button className="btn principal"
          disabled={ocupado || !listoParaEncolar || (p?.encolada && !p?.caducada)}
          onClick={() => acc.encolar(impl, logica)}>
          {acc.haciendo === "encolar" ? <Giro /> : t("1 · Anunciar el cambio")}
        </button>
        <button className="btn peligro"
          disabled={ocupado || !listoParaEncolar || !p?.lista}
          title={!p?.lista ? t("Todavía no ha pasado el plazo") : undefined}
          onClick={() => acc.ejecutar(impl, logica)}>
          {acc.haciendo === "ejecutar" ? <Giro /> : t("2 · Aplicarlo")}
        </button>
      </div>

      <p className="detalle">
        {t("Después de aplicarlo, los vaults que ya existen")} <strong>{t("no cambian")}</strong>: {t("siguen con la lógica con la que nacieron hasta que su dueño pulse «Actualizar la lógica del vault». Lo que cambia es con qué código nacen los nuevos.")}
      </p>
      <ModalOperacion estado={estadoOp} cerrar={acc.limpiar} red={red} />
    </section>
  );
}

/**
 * La cola del timelock: qué hay anunciado, cuánto le falta y el botón para aplicarlo.
 *
 * Cada fila lleva el calldata que se anunció, así que aplicar no exige volver a teclear las
 * direcciones — que es donde se cometía el error: el id es el hash de la llamada, y un carácter
 * distinto revierte con «no hay nada encolado» sobre una propuesta que existe.
 */
function ColaPendientes({
  cola, cargando, red, puedoAplicar, acc, graciaSegundos,
}: {
  cola: { propuestas: Pendiente[]; completa: boolean } | undefined;
  cargando: boolean;
  red: RedKeel;
  puedoAplicar: boolean;
  acc: ReturnType<typeof useAccionesGobierno>;
  graciaSegundos: number;
}) {
  const t = useT();
  const dias = Math.round(graciaSegundos / 86400);

  if (cargando) return <p className="detalle">{t("Leyendo la cola de {red}…", { red: red.etiqueta })}</p>;

  // Que la búsqueda de eventos se quedara corta NO es lo mismo que «no hay nada»: se dice, porque
  // sobre un gobierno con retardo un silencio equivocado es peor que un aviso.
  const parcial = cola && !cola.completa && (
    <div className="aviso error">
      <p className="detalle">
        {t("El RPC de {red} no me dejó recorrer todo el historial de anuncios, así que esta lista", { red: red.etiqueta })} <strong>{t("puede estar incompleta")}</strong>. {t("Compruébalo en el explorador antes de dar por hecho que no hay nada pendiente.")}
      </p>
    </div>
  );

  if (!cola || cola.propuestas.length === 0) {
    return (
      <div className="cola-gobierno">
        <h3>{t("Anuncios pendientes")}</h3>
        {parcial}
        <p className="detalle">
          {t("No hay nada anunciado en {red}. Cuando lo haya, aparecerá aquí con el tiempo que le falte para poder aplicarse.", { red: red.etiqueta })}
        </p>
      </div>
    );
  }

  return (
    <div className="cola-gobierno">
      <h3>{t("Anuncios pendientes")} · {cola.propuestas.length}</h3>
      {parcial}
      <ul className="lista-propuestas">
        {cola.propuestas.map((p) => {
          const firmando = acc.haciendo === `ejecutar:${p.id}`;
          return (
            <li key={p.id} className={p.caducada ? "caducada" : p.lista ? "lista" : "esperando"}>
              <div className="cabeza">
                <span className="que">
                  {(p.cedeElMando || p.destinoRaro) && <span className="grave" aria-hidden>⚠️ </span>}
                  {p.titulo}
                  {p.rival && (
                    <span className={p.masNueva ? "reciente" : "rival"}>
                      {p.masNueva ? t(" · la más reciente") : t(" · hay una más nueva anunciada")}
                    </span>
                  )}
                </span>
                {puedoAplicar && (
                  <button
                    className={`btn ${p.lista ? "principal" : ""}`}
                    disabled={!p.lista || !!acc.haciendo}
                    title={
                      p.caducada ? t("Caducó: hay que volver a anunciarla")
                        : !p.lista ? t("Todavía no ha pasado el retardo")
                          : undefined
                    }
                    onClick={() => acc.ejecutarLlamada(p.destino, p.datos, `ejecutar:${p.id}`)}
                  >
                    {firmando ? <Giro /> : t("Aplicar")}
                  </button>
                )}
              </div>

              <div className="estado">
                {p.caducada ? (
                  <>{t("Caducó el {fecha}. Hay que", { fecha: fecha(p.caducaEn) })} <strong>{t("volver a anunciarla")}</strong>.</>
                ) : p.lista ? (
                  <>
                    {t("Se puede aplicar")} <strong>{t("ya")}</strong>. {t("Caduca en")} <Cuenta hasta={p.caducaEn} />
                    {" "}({fecha(p.caducaEn)}).
                  </>
                ) : (
                  <>
                    {t("Faltan")} <Cuenta hasta={p.cuando} /> — {t("se podrá aplicar el {fecha}, y hay {dias} días para hacerlo antes de que caduque.", { fecha: fecha(p.cuando), dias })}
                  </>
                )}
              </div>

              {p.detalle.map((d) => (
                <div className="dato mono" key={d}>{d}</div>
              ))}

              {p.rival && (
                <div className="aviso">
                  <p className="detalle">
                    {p.masNueva ? (
                      <>
                        {t("Hay")} <strong>{t("otra propuesta viva más antigua")}</strong> {t("que también publica lógica, con otras direcciones. No se anulan entre sí: manda la última que apliques. Esta es")} <strong>{t("la anunciada más tarde")}</strong>; {t("deja caducar la otra.")}
                      </>
                    ) : (
                      <>
                        {t("Hay una propuesta")} <strong>{t("más reciente")}</strong> {t("que también publica lógica. Si aplicas esta")} <strong>{t("después")}</strong> {t("de aquella, vuelves atrás. Salvo que sea justo lo que quieres, deja que caduque.")}
                      </>
                    )}
                  </p>
                </div>
              )}
              {p.implDesconocida && (
                <div className="aviso error">
                  <p className="detalle">
                    {t("Esa implementación")} <strong>{t("no está en la lista que conoce la app")}</strong>{" "}
                    (<code>implsConGestion</code> {t("de {red}", { red: red.etiqueta })}). {t("Si se publica, los vaults que nazcan con ella se quedarán")} <strong>{t("sin los botones de gestión")}</strong>, {t("y no falla nada visible: simplemente no aparecen. Añádela a")}{" "}
                    <code>config/networks.ts</code> {t("antes de aplicar esto.")}
                  </p>
                </div>
              )}
              {p.destinoRaro && (
                <div className="aviso error">
                  <p className="detalle">
                    {t("El destino de esta llamada")} <strong>{t("no es la factory de {red}", { red: red.etiqueta })}</strong>,
                    {t("es")} <code>{p.destino}</code>. {t("Míralo en el explorador antes de aplicarla.")}
                  </p>
                </div>
              )}
              {p.cedeElMando && (
                <div className="aviso error">
                  <p className="detalle">
                    {t("Esta propuesta")} <strong>{t("cede el control")}</strong>. {t("Después de aplicarla, esta pantalla puede dejar de mandar sobre {red}.", { red: red.etiqueta })}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
